import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React from 'react'

import { TWhatsappFlowContent, TWhatsappFlowPage } from '@/app/types'

import WhatsappFlowElement from './whatsapp-flow-element'

type Props = {
  activePageId?: string
  content: TWhatsappFlowContent
  showPageNavigation?: boolean
  showPageTitle?: boolean
  onPageChange?: (page?: TWhatsappFlowPage) => void
}

const WhatsappFlowContent: React.FC<Props> = ({
  activePageId,
  content,
  showPageNavigation = true,
  showPageTitle = false,
  onPageChange,
}) => {
  const pages = content.pages || []
  const [pageIndex, setPageIndex] = React.useState(0)
  const currentPage = pages[pageIndex]
  const totalPages = pages.length

  const handleNext = React.useCallback(() => {
    setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))
  }, [totalPages])

  const handlePrev = React.useCallback(() => {
    setPageIndex((prev) => Math.max(prev - 1, 0))
  }, [])

  React.useEffect(() => {
    onPageChange?.(currentPage)
  }, [currentPage, onPageChange])

  React.useEffect(() => {
    if (!totalPages) {
      setPageIndex(0)
      return
    }

    setPageIndex((previousPageIndex) => Math.min(previousPageIndex, totalPages - 1))
  }, [totalPages])

  React.useEffect(() => {
    if (!activePageId) {
      return
    }

    const index = pages.findIndex((page) => page.id === activePageId)
    if (index >= 0) {
      setPageIndex(index)
    }
  }, [activePageId, pages])

  return (
    <Stack spacing={2}>
      {!totalPages && (
        <Typography color="textSecondary" variant="body2">
          No pages defined yet.
        </Typography>
      )}
      {currentPage && (
        <>
          {(showPageTitle || !!currentPage.subtitle || totalPages > 1) && (
            <Stack spacing={0.5}>
              {showPageTitle && (
                <Typography noWrap title={currentPage.title} variant="subtitle1">
                  {currentPage.title}
                </Typography>
              )}
              {currentPage.subtitle && (
                <Typography color="textSecondary" variant="caption">
                  {currentPage.subtitle}
                </Typography>
              )}
              {totalPages > 1 && (
                <Typography color="textSecondary" variant="caption">
                  Page {pageIndex + 1} of {totalPages}
                </Typography>
              )}
            </Stack>
          )}
          <Stack spacing={2}>
            {currentPage.elements.map((element, index) => (
              <WhatsappFlowElement key={`${currentPage.id}-${element.questionId ?? index}`} element={element} />
            ))}
          </Stack>
        </>
      )}
      {showPageNavigation && totalPages > 1 && (
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Button disabled={pageIndex === 0} size="small" onClick={handlePrev}>
            Back
          </Button>
          <Button disabled={pageIndex >= totalPages - 1} size="small" onClick={handleNext}>
            Next
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export default WhatsappFlowContent
